/**
 * Houdt blokken gelijk aan de gekozen variant.
 *
 * HET PROBLEEM
 * Horizon ververst bij een variantwissel standaard alleen de variantkiezer
 * ("By default, only the variant picker is morphed", variant-picker.js).
 * Onderdelen daarbuiten moeten zichzelf bijwerken. De plakbalk onderin doet
 * dat al; het prijsblok en het staffelblok deden het niet. Gevolg op een
 * product met varianten van verschillende prijs: de grote prijs bleef op de
 * variant staan die bij het laden actief was, en — erger — de keuzemenu's in
 * het staffelblok ook. Wie de dure variant koos en er twee nam, kreeg er twee
 * van de goedkope in zijn winkelwagen, tegen de goedkope prijs. Nagemeten op
 * 1 okt 2026: 285 van de 1762 producten hebben varianten die onderling in
 * prijs verschillen.
 *
 * DE AANPAK: NIETS ZELF UITREKENEN
 * Bij elke wissel haalt Horizon al een verse versie van de sectie bij de
 * server op en hangt die aan het shopify:product:select-event. Daar staat
 * alles al goed in: prijs, doorhaling, badge, elke staffeltrede en elk
 * keuzemenu, gerenderd door dezelfde Liquid als bij het laden van de pagina.
 * Dit bestand pakt die blokken eruit en zet ze in de plaats van de oude.
 *
 * Dat is met opzet geen herberekening in JavaScript. Zou dit bestand de
 * prijzen zelf uitrekenen, dan bestaat er een tweede som naast die in
 * blocks/mivoza-tiers.liquid, en elk verschil tussen die twee is een verkeerd
 * bedrag op het scherm. Nu is er één som, op de server.
 *
 * WAAROM HET HELE ELEMENT EN NIET DE INHOUD
 * <mivoza-tiers> legt in connectedCallback de serverbedragen vast in
 * #origineel en zet die terug bij een aantalwissel. Morph je alleen de inhoud,
 * dan blijft die map op de vorige variant staan en komen de oude bedragen bij
 * de eerstvolgende aantalwissel terug. Door het element zelf te vervangen
 * draait connectedCallback opnieuw en legt het de juiste stand vast.
 *
 * De trede die de klant had gekozen wordt bewaard en daarna opnieuw
 * aangeklikt, zodat een variantwissel zijn aantal niet terugzet.
 */

/** Blokken die zichzelf niet bijwerken. De plakbalk staat er niet bij: die regelt het zelf. */
const BLOKKEN = ['.mv-price', 'mivoza-tiers'];

/** Binnen deze sectie werken we; daarbuiten (kaartjes, snelkoopvenster) niet. */
const HOOFD = 'product-information, .product-information';

/**
 * @param {Document} verseHtml
 * @param {string} kies
 */
function vervangBlok(verseHtml, kies) {
  const hoofd = document.querySelector(HOOFD) || document;
  const oud = hoofd.querySelector(kies);
  const nieuw = verseHtml.querySelector(kies);
  if (!oud || !nieuw) return;

  // De gekozen trede onthouden, zodat het aantal een variantwissel overleeft.
  const tredeVoor = oud.querySelector('.mv-tier.is-on')?.getAttribute('data-qty') || null;

  // Uit een los document: eerst binnenhalen, anders hoort de node bij de
  // verkeerde document en lopen custom elements niet op.
  oud.replaceWith(document.importNode(nieuw, true));

  if (!tredeVoor) return;
  const terug = hoofd.querySelector(`${kies} .mv-tier[data-qty="${tredeVoor}"] input`);
  if (terug instanceof HTMLInputElement && !terug.checked) terug.click();
}

/** @param {Event & {promise?: Promise<any>, detail?: any}} event */
function bijVariantKeuze(event) {
  const doel = event.target;
  if (!(doel instanceof Element)) return;
  // Hetzelfde event komt ook van productkaartjes en het snelkoopvenster.
  if (doel.closest('product-card') || doel.closest('quick-add-dialog')) return;
  if (!(event.promise instanceof Promise)) return;

  event.promise
    .then((uitkomst) => {
      const detail = uitkomst?.detail;
      const verseHtml = detail?.html;
      if (!verseHtml) return;

      // Gaat het wel over het product op deze pagina?
      const hier = document.querySelector(`${HOOFD} variant-picker`)?.getAttribute('data-product-id');
      if (hier && detail.productId && String(detail.productId) !== String(hier)) return;

      for (const kies of BLOKKEN) vervangBlok(verseHtml, kies);
    })
    .catch((fout) => {
      // Een afgebroken verzoek is normaal als iemand snel doorklikt.
      if (fout?.name !== 'AbortError') console.warn('[mivoza-variant-sync]', fout);
    });
}

document.addEventListener('shopify:product:select', bijVariantKeuze, true);
